import React, { useCallback, useEffect, useRef, useState } from "react";
import { Building2, Check, ChevronDown, Loader2, Plus, RefreshCw, Clock, MailCheck } from "lucide-react";
import defaultOrgLogo from "@/assets/images/logo.png";
import { organizationApi } from "@/api/organization/organizationApi.js";
import { useAuth } from "@/context/AuthContext.jsx";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import AddPropertyModal, { getStoredPendingRequests } from "./AddPropertyModal";

const getCapabilityBadge = (org) => {
  const caps = org?.capabilities || org?.Capabilities;
  if (caps?.hotel || caps?.Hotel) return "Hotel / PMS";
  if (caps?.pos || caps?.Pos) return "POS only";
  const t = (org?.organizationType || org?.OrganizationType || "").toLowerCase().trim();
  if (t === "hotel") return "Hotel / PMS";
  if (t === "food & beverages") return "Food & Beverages";
  return org?.organizationType || org?.OrganizationType || "Organization";
};

const orgCanAccessPortal = (org) => {
  if (org?.isPending) return false;
  const caps = org?.capabilities || org?.Capabilities;
  if (
    caps &&
    (caps.hotel !== undefined ||
      caps.Hotel !== undefined ||
      caps.pos !== undefined ||
      caps.Pos !== undefined)
  ) {
    return Boolean(caps.hotel ?? caps.Hotel) || Boolean(caps.pos ?? caps.Pos);
  }
  // Older payloads without capability flags: allow and let the server decide.
  return true;
};

const sameOrgId = (left, right) =>
  String(left || "").toLowerCase() === String(right || "").toLowerCase();

/**
 * OrganizationSwitcher
 *
 * Displays active organization and linked properties.
 * Includes "+ Add Property" button to register new properties or request access to existing ones.
 */
export default function OrganizationSwitcher({ compact = false }) {
  const { organizationId, organizationName, organizationType, organizationCapabilities, switchOrganization } = useAuth();

  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [pendingRequests, setPendingRequests] = useState(() => getStoredPendingRequests());
  const [showAddModal, setShowAddModal] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [switching, setSwitching] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const fetchingRef = useRef(false);

  const reloadPending = useCallback(() => {
    setPendingRequests(getStoredPendingRequests());
  }, []);

  useEffect(() => {
    window.addEventListener("hotel:pendingRequestsUpdated", reloadPending);
    return () => {
      window.removeEventListener("hotel:pendingRequestsUpdated", reloadPending);
    };
  }, [reloadPending]);

  const fetchOrgs = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setFetching(true);
    setFetchError(false);
    try {
      const response = await organizationApi.getMyOrganizations();
      const data = response?.data?.data || response?.data || [];
      setOrgs(Array.isArray(data) ? data : []);
      setFetched(true);
    } catch (err) {
      console.error("Failed to fetch organizations", err);
      setFetchError(true);
    } finally {
      fetchingRef.current = false;
      setFetching(false);
    }
  }, []);

  const handleOpenChange = (nextOpen) => {
    if (nextOpen && !fetched) {
      fetchOrgs();
    }
    setOpen(nextOpen);
  };

  const handleSwitch = async (org) => {
    if (org.isPending) {
      toast.info(org.pendingNotice || "This request is pending approval.");
      return;
    }
    if (sameOrgId(org.organizationId, organizationId) || switching) {
      return;
    }
    if (!orgCanAccessPortal(org)) {
      toast.error("This organization is not enabled for Hotel or POS features.");
      return;
    }
    setSwitching(org.organizationId);
    const result = await switchOrganization(org.organizationId);
    if (!result.success) {
      setSwitching(null);
      toast.error(result.message || "Failed to switch organization");
    }
  };

  const activeBadge = getCapabilityBadge({
    organizationType,
    capabilities: organizationCapabilities,
  });

  if (!organizationName && !organizationId) return null;

  // Combine fetched active organizations with pending local requests (deduplicating by organizationId)
  const activeOrgIds = new Set(orgs.map((o) => String(o.organizationId).toLowerCase()));
  const pendingFormatted = pendingRequests
    .filter((req) => !activeOrgIds.has(String(req.organizationId).toLowerCase()))
    .map((req) => ({
      organizationId: req.organizationId,
      organizationName: req.organizationName,
      organizationType: req.organizationType || "Hotel / PMS",
      organizationCode: req.organizationCode || "",
      isPending: true,
      pendingType: req.status,
      badgeText: req.badgeText,
      pendingNotice:
        req.status === "pending_superadmin"
          ? "This property is pending SuperAdmin approval."
          : "Access request is awaiting property owner approval.",
    }));

  const allDisplayOrgs = [...orgs, ...pendingFormatted];

  return (
    <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id="org-switcher-trigger"
            type="button"
            variant="ghost"
            className={cn(
              "group h-auto min-h-10 w-full rounded-md px-1.5 py-1.5",
              compact ? "justify-center" : "justify-between"
            )}
            aria-label="Switch organization"
          >
            <div className={cn("min-w-0", compact && "flex justify-center")}>
              {compact && (
                <span className="flex size-8 items-center justify-center text-primary">
                  {switching ? <Loader2 className="animate-spin" /> : <Building2 />}
                </span>
              )}
              {!compact && (
                <div className="min-w-0 text-left">
                  <p className="truncate text-sm font-semibold leading-5">
                    {organizationName || "Select organization"}
                  </p>
                  <p className="truncate text-xs font-normal text-muted-foreground">{activeBadge}</p>
                </div>
              )}
            </div>

            {!compact && (
              <ChevronDown
                className={cn(
                  "text-muted-foreground transition-transform duration-200",
                  open && "rotate-180"
                )}
              />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-80 overflow-hidden rounded-xl border-border bg-popover p-0 shadow-xl"
        >
          <div className="border-b border-border px-3.5 py-2.5 flex items-center justify-between">
            <p className="text-sm font-semibold">Organizations</p>
            <span className="text-[11px] text-muted-foreground font-medium">
              {orgs.length} Active
            </span>
          </div>

          <div
            role="listbox"
            aria-label="Organizations"
            className="flex max-h-72 flex-col gap-1 overflow-y-auto p-1.5 [scrollbar-width:thin]"
          >
            {fetching && (
              <>
                <span className="sr-only">Loading organizations</span>
                {[0, 1].map((item) => (
                  <div key={item} className="flex items-center gap-2.5 px-2 py-2">
                    <Skeleton className="size-8 shrink-0 rounded-md" />
                    <div className="flex flex-1 flex-col gap-2">
                      <Skeleton className="h-3.5 w-2/3" />
                      <Skeleton className="h-3 w-1/3" />
                    </div>
                  </div>
                ))}
              </>
            )}

            {!fetching && fetchError && (
              <div className="flex flex-col items-center gap-2 px-4 py-5 text-center">
                <p className="text-sm font-medium">Could not load organizations</p>
                <Button type="button" variant="outline" size="sm" onClick={fetchOrgs}>
                  Try again
                </Button>
              </div>
            )}

            {!fetching && !fetchError && fetched && allDisplayOrgs.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                No properties found.
              </p>
            )}

            {!fetching &&
              !fetchError &&
              allDisplayOrgs.map((org) => {
                const isActive = sameOrgId(org.organizationId, organizationId);
                const badge = getCapabilityBadge(org);
                const isSwitching = sameOrgId(switching, org.organizationId);
                const hotelEnabled = orgCanAccessPortal(org);
                const isPending = Boolean(org.isPending);

                return (
                  <Button
                    key={org.organizationId}
                    role="option"
                    aria-selected={isActive}
                    type="button"
                    variant="ghost"
                    onClick={() => handleSwitch(org)}
                    disabled={Boolean(switching) || (!hotelEnabled && !isActive) || isPending}
                    className={cn(
                      "h-auto min-h-12 w-full justify-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors",
                      isActive && "bg-primary/10 text-foreground hover:bg-primary/10 font-medium",
                      isPending && "bg-muted/30 border border-dashed border-border/70 opacity-80 cursor-not-allowed",
                      !hotelEnabled && !isActive && !isPending && "opacity-60"
                    )}
                  >
                    <span className={cn(
                      "flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md text-muted-foreground",
                      isPending
                        ? org.pendingType === "pending_superadmin"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-blue-100 text-blue-700"
                        : "bg-muted"
                    )}>
                      {isPending ? (
                        org.pendingType === "pending_superadmin" ? <Clock className="h-4 w-4" /> : <MailCheck className="h-4 w-4" />
                      ) : (
                        <img
                          src={org.appLogo || org.imageUrl || defaultOrgLogo}
                          alt=""
                          className="size-full object-contain p-0.5"
                        />
                      )}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium leading-5">
                        {org.organizationName}
                      </span>
                      <span className="flex min-w-0 items-center gap-1.5 text-xs font-normal text-muted-foreground">
                        {isPending ? (
                          <span className={cn(
                            "inline-flex items-center gap-1 text-[11px] font-medium leading-tight px-1.5 py-0.5 rounded-md",
                            org.pendingType === "pending_superadmin"
                              ? "bg-amber-100/80 text-amber-800"
                              : "bg-blue-100/80 text-blue-800"
                          )}>
                            {org.badgeText}
                          </span>
                        ) : (
                          <>
                            <span className="truncate">
                              {hotelEnabled ? badge : "App access disabled"}
                            </span>
                            {org.organizationCode && (
                              <span className="truncate">({org.organizationCode})</span>
                            )}
                          </>
                        )}
                      </span>
                    </span>

                    {isSwitching ? (
                      <Loader2 className="animate-spin text-primary shrink-0" />
                    ) : isActive ? (
                      <span className="flex shrink-0 text-primary" aria-label="Current organization">
                        <Check className="h-4 w-4" />
                      </span>
                    ) : null}
                  </Button>
                );
              })}
          </div>

          <div className="border-t border-border p-2 flex flex-col gap-1.5 bg-muted/20">
            <Button
              id="add-property-btn"
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
                setShowAddModal(true);
              }}
              disabled={fetching || Boolean(switching)}
              className="h-9 w-full justify-center gap-2 rounded-lg border-dashed border-primary/40 bg-primary/5 text-xs font-semibold text-primary hover:bg-primary/10 hover:border-primary/60 transition-all"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Property
            </Button>

            {!fetchError && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setFetched(false);
                  fetchOrgs();
                }}
                disabled={fetching || Boolean(switching)}
                className="h-7 w-full justify-center gap-1.5 rounded-md text-[11px] text-muted-foreground hover:text-foreground"
              >
                <RefreshCw className={cn("h-3 w-3", fetching && "animate-spin")} />
                Refresh list
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Add Property Modal */}
      <AddPropertyModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        onRequestSubmitted={(newItem) => {
          setPendingRequests(getStoredPendingRequests());
        }}
      />
    </>
  );
}
